// src/layouts/SharedComponents/WebcamCaptureModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Segmented, Spin } from 'antd';
import { CameraOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const WebcamCaptureModal = ({ open, onCancel, onCapture, defaultFacing = 'environment', cam }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const nativeInputRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [facingMode, setFacingMode] = useState(defaultFacing); // 'user' = front, 'environment' = back

  // ─────────────────────────────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────────────────────────────
  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(
    async () => {
      setIsLoading(true);
      setError(null);

      // Stop any existing stream first
      stopStream();

      try {
        const constraints = {
          video: { facingMode: facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        // Detect if multiple cameras are available
        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = allDevices.filter(device => device.kind === 'videoinput');
        setHasMultipleCameras(videoDevices.length > 1);

        setIsLoading(false);
      } catch (err) {
        console.error('Error accessing webcam:', err);
        setIsLoading(false);

        if (err.name === 'NotAllowedError') {
          setError(translateX('camera_permission_denied'));
        } else if (err.name === 'NotFoundError') {
          setError(translateX('camera_not_found'));
        } else {
          setError(translateX('camera_error'));
        }
      }
    },
    [facingMode, stopStream]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // fullScreen mode: trigger native camera input instead of in-modal video
  useEffect(() => {
    if (cam === 'fullScreen' && open) {
      // Small delay to ensure the hidden input is rendered
      const timer = setTimeout(() => nativeInputRef.current?.click(), 50);
      return () => clearTimeout(timer);
    }
  }, [cam, open]);

  // Default mode: start/stop video stream
  useEffect(() => {
    if (cam === 'fullScreen') return;

    if (open) {
      startCamera();
    } else {
      stopStream();
    }

    return () => stopStream();
  }, [open, cam, startCamera, stopStream]);

  // ─────────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Set canvas size to video size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // If using front camera (selfie), mirror the captured image to match what user saw
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }

    // Draw current video frame to canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Reset transform
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // Convert canvas to blob
    canvas.toBlob(
      blob => {
        if (blob) {
          // Create a File object from the blob
          const file = new File([blob], `webcam-capture-${Date.now()}.jpg`, {
            type: 'image/jpeg',
          });

          // Stop the camera stream
          stopStream();

          // Pass the file to parent component
          onCapture?.(file);
        }
      },
      'image/jpeg',
      0.92
    );
  };

  const handleRetry = () => {
    startCamera();
  };

  const handleCancel = () => {
    stopStream();
    onCancel?.();
  };

  // fullScreen mode: native camera file selected
  const handleNativeCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onCapture?.(file);
    } else {
      // User cancelled native camera — notify parent
      onCancel?.();
    }
    if (nativeInputRef.current) nativeInputRef.current.value = '';
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────

  // fullScreen mode: only render hidden input that triggers native camera
  if (cam === 'fullScreen') {
    return (
      <input
        ref={nativeInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleNativeCapture}
        style={{ display: 'none' }}
      />
    );
  }

  return (
    <Modal
      title={translateX('take_photo')}
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={640}
      destroyOnHidden
      centered
    >
      <div style={{ position: 'relative', minHeight: 360 }}>
        {/* Loading state */}
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f5f5f5',
              borderRadius: 8,
            }}
          >
            {/* Ant Design v6: tip only works with nested children pattern */}
            <Spin size="large" tip={translateX('loading_camera')}>
              <div style={{ padding: '50px 100px' }} />
            </Spin>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f5f5f5',
              borderRadius: 8,
              padding: 24,
              textAlign: 'center',
            }}
          >
            <CameraOutlined style={{ fontSize: 48, color: '#999', marginBottom: 16 }} />
            <p style={{ color: '#666', marginBottom: 16 }}>{error}</p>
            <Button icon={<ReloadOutlined />} onClick={handleRetry}>
              {translateX('try_again')}
            </Button>
          </div>
        )}

        {/* Video preview */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            maxHeight: 480,
            borderRadius: 8,
            background: '#000',
            display: isLoading || error ? 'none' : 'block',
            transform: facingMode === 'user' ? 'scaleX(-1)' : 'none', // Mirror preview for selfie only
          }}
        />

        {/* Hidden canvas for capture */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </div>

      {/* Controls */}
      {!isLoading && !error && (
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
          {/* Front/Back camera toggle (only if multiple cameras detected) */}
          {hasMultipleCameras && (
            <Segmented
              value={facingMode}
              onChange={setFacingMode}
              options={[
                { value: 'environment', icon: <CameraOutlined /> },
                { value: 'user', icon: <UserOutlined /> },
              ]}
            />
          )}

          {/* Capture button */}
          <Button type="primary" icon={<CameraOutlined />} size="large" onClick={handleCapture}>
            {translateX('capture')}
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default WebcamCaptureModal;
